import React, { useState } from 'react'
import {
  Button,
  Text,
  Link,
  HStack,
  Box,
  VStack,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogHeader,
  DialogBody,
} from '@chakra-ui/react'
import TermsOfServiceContent from './TermsOfServiceContent'

// Modal using Chakra Dialog with proper z-index and positioning for overlay stacking
const CustomModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'md' | 'lg' | 'xl';
}> = ({ isOpen, onClose, title, children, size = 'xl' }) => {
  const dialogSize = (size === 'xl' ? 'xl' : size) as any
  return (
    <DialogRoot 
      open={isOpen} 
      onOpenChange={(e) => (e.open ? null : onClose())} 
      placement="center" 
      size={{ base: 'sm', md: dialogSize }}
      lazyMount
      unmountOnExit
    >
      <DialogBackdrop 
        bg="blackAlpha.600" 
        backdropFilter="blur(4px)" 
        position="fixed"
        inset={0}
        zIndex={1500} 
      />
      <DialogPositioner 
        position="fixed"
        inset={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        zIndex={1600}
      >
        <DialogContent zIndex={1700} bg="white">
          <DialogHeader>
            <HStack justify="space-between" align="center">
              <Text fontSize="lg" fontWeight="semibold">{title}</Text>
              <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
                ×
              </Button>
            </HStack>
          </DialogHeader>
          <DialogBody p={0}>{children}</DialogBody>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}

interface TermsOfServiceModalProps {
  isOpen: boolean
  onClose: () => void
  onAccept?: () => void
  onDecline?: () => void
  showAcceptDecline?: boolean
  title?: string
  acceptText?: string
  declineText?: string
}

export const TermsOfServiceModal: React.FC<TermsOfServiceModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  onDecline,
  showAcceptDecline = false,
  title = "Terms of Service",
  acceptText = "Accept",
  declineText = "Decline"
}) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10 // 10px tolerance
    setHasScrolledToBottom(isAtBottom)
  }

  const handleAccept = () => {
    if (onAccept) {
      onAccept()
    }
    onClose()
  }

  const handleDecline = () => {
    if (onDecline) {
      onDecline()
    }
    onClose()
  }

  return (
    <CustomModal isOpen={isOpen} onClose={onClose} title={title} size="xl">
      <VStack gap={4} align="stretch" p={6}>
        <Box overflowY="auto" onScroll={handleScroll} maxH="60vh" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
          <TermsOfServiceContent />
        </Box>

        {showAcceptDecline && (
          <HStack gap={4} w="full" justify="space-between">
            <Text fontSize="sm" color="gray.600">
              {!hasScrolledToBottom && "Please scroll to read the full terms"}
            </Text>
            <HStack gap={3}>
              <Button
                variant="outline"
                onClick={handleDecline}
                colorScheme="gray"
              >
                {declineText}
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleAccept}
                disabled={!hasScrolledToBottom}
              >
                {acceptText}
              </Button>
            </HStack>
          </HStack>
        )}

        {!showAcceptDecline && (
          <HStack gap={4} w="full" justify="space-between">
            <Text fontSize="sm" color="gray.600">
              For questions about these terms, contact{' '}
              <Link href="mailto:support@livewello.com" color="blue.500">
                support@livewello.com
              </Link>
            </Text>
            <Button colorScheme="blue" onClick={onClose}>
              Close
            </Button>
          </HStack>
        )}
      </VStack>
    </CustomModal>
  )
}

export default TermsOfServiceModal
